import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  attendanceStatuses,
  type AttendanceBatchView,
  type AttendanceStatus,
  type EventParticipantView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  applyBatchResults,
  attendanceKeys,
  changedItems,
  chooseStatus,
  markOpenRowsPresent,
  pendingCount,
  rsvpKeys,
  sheetRows,
  shownStatus,
  type AttendanceRow,
} from './scheduling';

export interface AttendanceSheetProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  eventId: string;
  /** Whether the person may record attendance; others see the sheet read-only. */
  canRecord: boolean;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

const recordableStatuses = attendanceStatuses.filter((status) => status !== 'not_recorded');

/**
 * One tap per athlete. Choices stay on the sheet until saved, with a visible unsaved indicator, and
 * a failed save keeps them. A row another coach changed first comes back showing their entry
 * instead of being overwritten.
 */
export function AttendanceSheet({
  locale,
  base,
  csrfToken,
  eventId,
  canRecord,
}: AttendanceSheetProps): React.JSX.Element {
  const [rows, setRows] = useState<AttendanceRow[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const [conflicts, setConflicts] = useState(0);
  const [saving, setSaving] = useState(false);
  const sheetPath = `${base}/events/${eventId}/attendance`;

  const load = useCallback(async () => {
    try {
      const participants = await apiRequest<EventParticipantView[]>(sheetPath);
      setRows(sheetRows(participants, locale));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [sheetPath, locale]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    if (rows === null) return;
    setErrorKey(null);
    setSaved(false);
    setSaving(true);
    try {
      const outcome = await apiRequest<AttendanceBatchView>(sheetPath, {
        method: 'PATCH',
        body: { items: changedItems(rows) },
        csrfToken,
      });
      setRows(applyBatchResults(rows, outcome.results));
      setConflicts(outcome.conflicts);
      setSaved(outcome.conflicts === 0);
    } catch (error) {
      // Nothing is confirmed: the choices stay on the sheet, marked as unsaved.
      setErrorKey(errorKeyOf(error));
    } finally {
      setSaving(false);
    }
  }

  const unsaved = rows === null ? 0 : pendingCount(rows);

  return (
    <Box
      component="section"
      aria-label={translate(locale, 'calendar.attendance.title')}
      sx={{ mt: 2 }}
    >
      <Typography variant="subtitle1" component="h4" gutterBottom>
        {translate(locale, 'calendar.attendance.title')}
      </Typography>
      {!canRecord && (
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          {translate(locale, 'calendar.attendance.readOnly')}
        </Typography>
      )}
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && unsaved === 0 && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {translate(locale, 'calendar.attendance.saved')}
        </Alert>
      )}
      {conflicts > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {translate(locale, 'calendar.attendance.conflictSummary')} {conflicts}
        </Alert>
      )}
      {rows === null && errorKey === null && (
        <Typography>{translate(locale, 'calendar.loading')}</Typography>
      )}
      {rows?.length === 0 && (
        <Typography color="text.secondary">
          {translate(locale, 'calendar.participants.empty')}
        </Typography>
      )}
      {canRecord && rows !== null && rows.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 1, flexWrap: 'wrap', alignItems: 'center', gap: 1 }}
        >
          <Button
            variant="outlined"
            sx={{ minHeight: 44 }}
            onClick={() => {
              setRows(markOpenRowsPresent(rows));
              setSaved(false);
            }}
          >
            {translate(locale, 'calendar.attendance.markAll')}
          </Button>
          <Button
            variant="contained"
            sx={{ minHeight: 44 }}
            disabled={unsaved === 0 || saving}
            onClick={() => {
              void save();
            }}
          >
            {translate(locale, 'calendar.attendance.save')}
          </Button>
          {unsaved > 0 && (
            <Chip
              color="warning"
              label={`${translate(locale, 'calendar.attendance.unsaved')}: ${String(unsaved)}`}
            />
          )}
        </Stack>
      )}
      <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {rows?.map((row) => (
          <Box
            component="li"
            key={row.participantId}
            sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
              <Typography sx={{ fontWeight: 500 }}>{row.name}</Typography>
              <Chip
                size="small"
                variant="outlined"
                label={translate(
                  locale,
                  row.rsvp === null ? 'calendar.rsvp.none' : rsvpKeys[row.rsvp],
                )}
              />
              {row.pending !== null && (
                <Chip
                  size="small"
                  color="warning"
                  label={translate(locale, 'calendar.attendance.unsaved')}
                />
              )}
            </Box>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              disabled={!canRecord}
              color="primary"
              aria-label={`${translate(locale, 'calendar.attendance.title')}: ${row.name}`}
              value={shownStatus(row) === 'not_recorded' ? null : shownStatus(row)}
              onChange={(_event, value: AttendanceStatus | null) => {
                if (value === null) return;
                setRows(chooseStatus(rows, row.participantId, value));
                setSaved(false);
              }}
              sx={{ flexWrap: 'wrap' }}
            >
              {recordableStatuses.map((status) => (
                <ToggleButton
                  key={status}
                  value={status}
                  sx={{ minHeight: 44, minWidth: 44, flex: '1 1 72px' }}
                >
                  {translate(locale, attendanceKeys[status])}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {row.conflict && (
              <Alert severity="warning" sx={{ mt: 0.5 }}>
                {translate(locale, 'calendar.attendance.conflict')}
              </Alert>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
